import React, { useEffect, useMemo, useState } from 'react'
import { Form, Modal } from 'react-bootstrap'
import { Controller, useForm } from 'react-hook-form'
import PhoneInput from 'react-phone-input-2'
import { useLocation, useNavigate } from 'react-router-dom'
import LoadingButtonWithForm from '../../components/LoadingButtonWithForm'
import { responseError } from '../../helpers/responseHandler'
import { useAddDriverMutation, useEditDriverMutation } from '../../redux/services/DriverService'
import { sweetAlert } from '../../utils'

const DriversAddModal = ({ show, handleClose, isEdits }) => {
  const navigate = useNavigate()
  const {
    state: { isEdit },
  } = useLocation()

  let {
    control,
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm()

  const [addRequest, { isLoading: addLoading }] = useAddDriverMutation()
  const [editRequest, { isLoading: editLoading }] = useEditDriverMutation()

  const [countries, setCountries] = useState([])
  const [states, setStates] = useState([])
  const [cities, setCities] = useState([])
  const [selectedCountry, setSelectedCountry] = useState('')
  const [selectedState, setSelectedState] = useState('')

  useEffect(() => {
    fetch('https://countriesnow.space/api/v0.1/countries/')
      .then(response => response.json())
      .then(data => {
        setCountries(data.data.map(country => country.country))
      })
      .catch(error => console.error('Error fetching countries:', error))
  }, [])

  const handleCountryChange = country => {
    setSelectedCountry(country)
    setSelectedState('') // Reset state and city when country changes
    setCities([])

    fetch(`https://countriesnow.space/api/v0.1/countries/states/q?country=${country}`)
      .then(response => response.json())
      .then(data => {
        setStates(data?.data?.states || [])
      })
      .catch(error => console.error('Error fetching states:', error))
  }

  const handleStateChange = state => {
    setSelectedState(state)

    fetch(
      `https://countriesnow.space/api/v0.1/countries/state/cities/q?country=${
        selectedCountry || isEdit?.country
      }&state=${state}`
    )
      .then(response => response.json())
      .then(data => {
        setCities(data?.data || [])
      })
      .catch(error => console.error('Error fetching cities:', error))
  }

  const country = useMemo(() => isEdit?.country, [])
  const statess = useMemo(() => isEdit?.state, [])

  useEffect(() => {
    if (country) {
      handleCountryChange(country)
    }
  }, [country])

  useEffect(() => {
    if (statess) {
      setTimeout(() => {
        handleStateChange(statess)
      }, [])
    }
  }, [statess])

  const onSubmit = async data => {
    if (isEdit) {
      const newForm = { ...data }
      await editRequest({ id: isEdit?.id, newForm })
        .unwrap()
        .then(res => {
          if (res?.status) {
            sweetAlert(res?.message, 'success')
            navigate(-1)
          }
        })
        .catch(err => {
          responseError(err)
        })
    } else {
      const newForm = { ...data }
      await addRequest(newForm)
        .unwrap()
        .then(res => {
          if (res?.status) {
            sweetAlert(res?.message, 'success')
            navigate(-1)
          }
        })
        .catch(err => {
          responseError(err)
        })
    }
  }

  return (
    <>
      <Modal show={true} onHide={() => navigate(-1)}>
        <Modal.Header className='border-0' closeButton>
          <Modal.Title className='h5'>{isEdit ? 'Edit' : 'Add'} Driver</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit(onSubmit)} id='driver-status'>
            <Form.Group className='mb-3'>
              <Form.Label>Full Name:</Form.Label>
              <Controller
                name='full_name'
                control={control}
                defaultValue={isEdit?.full_name || ''}
                rules={{
                  required: true,
                }}
                render={({ field }) => (
                  <Form.Control
                    {...field}
                    type='text'
                    placeholder='Enter Full Name'
                    isInvalid={errors.full_name}
                  />
                )}
              />
            </Form.Group>

            <Form.Group className='mb-3'>
              <Form.Label>Email Address</Form.Label>
              <Controller
                name='email'
                control={control}
                defaultValue={isEdit?.email || ''}
                rules={{
                  required: true,
                }}
                render={({ field }) => (
                  <Form.Control
                    {...field}
                    type='text'
                    placeholder='Enter Email Address'
                    isInvalid={errors.email}
                    disabled={isEdit?.email ? true : false}
                  />
                )}
              />
              <Form.Group className='mb-3'>
                <Form.Label>Driving License Number:</Form.Label>
                <Controller
                  name='dln'
                  control={control}
                  defaultValue={isEdit?.dln || ''}
                  rules={{
                    required: false,
                  }}
                  render={({ field }) => (
                    <Form.Control
                      {...field}
                      type='text'
                      placeholder='Driving License Number'
                      isInvalid={errors.dln}
                    />
                  )}
                />
              </Form.Group>
              <Form.Group className='mb-3'>
                <Controller
                  name='check'
                  control={control}
                  defaultValue={isEdit?.check ?? true}
                  render={({ field }) => (
                    <Form.Check
                      type='switch'
                      id='custom-switch'
                      label='Check to Send Sms & Email'
                      checked={field.value}
                      onChange={e => field.onChange(e.target.checked)}
                    />
                  )}
                />
              </Form.Group>
            </Form.Group>

            <Form.Group className='mb-3'>
              <Form.Label>Phone Number:</Form.Label>
              <Controller
                name='phone'
                control={control}
                defaultValue={isEdit?.phone || '+1'}
                rules={{
                  required: true,
                }}
                render={({ field }) => (
                  <PhoneInput
                    {...field}
                    country={'us'} // Default country
                    inputProps={{
                      name: 'phone',
                      required: true,
                      autoFocus: true,
                    }}
                  />
                )}
              />
            </Form.Group>

            <Form.Group className='mb-3'>
              <Form.Label>Date of Birth:</Form.Label>
              <Controller
                name='dob'
                control={control}
                defaultValue={isEdit?.dob || ''}
                rules={{
                  required: false,
                }}
                render={({ field }) => (
                  <Form.Control
                    {...field}
                    type='date'
                    placeholder='Enter Date of Birth'
                    isInvalid={errors.dob}
                  />
                )}
              />
            </Form.Group>

            <Form.Group className='mb-3'>
              <Form.Label>Address line 1:</Form.Label>
              <Controller
                name='address_1'
                control={control}
                defaultValue={isEdit?.address_1 || ''}
                rules={{
                  required: false,
                }}
                render={({ field }) => (
                  <Form.Control
                    {...field}
                    as='textarea'
                    rows={1}
                    placeholder='Enter address line 1'
                    isInvalid={errors.address_1}
                  />
                )}
              />
            </Form.Group>
            <Form.Group className='mb-3'>
              <Form.Label>address line 2:</Form.Label>
              <Controller
                name='address_2'
                control={control}
                defaultValue={isEdit?.address_2 || ''}
                rules={{
                  required: false,
                }}
                render={({ field }) => (
                  <Form.Control
                    {...field}
                    as='textarea'
                    rows={1}
                    placeholder='Enter address line 2'
                    isInvalid={errors.address_2}
                  />
                )}
              />
            </Form.Group>

            {/* <Form.Group className="mb-3">
                            <Form.Label>Country:</Form.Label>

                            <Form.Control className='form-select' as="select" name="country" onChange={handleCountryChange} value={selectedCountry}>
                                <option value="">Select Country</option>
                                {countries.map((country, index) => (
                                    <option key={index} value={country}>{country}</option>
                                ))}
                            </Form.Control>
                        </Form.Group>



                        <Form.Group className="mb-3">
                            <Form.Label>State:</Form.Label>

                            <Form.Control className='form-select' as="select" name="state" onChange={handleStateChange} value={selectedState}>
                                <option value="">Select State</option>
                                {states.map((state, index) => (
                                    <option key={index} value={state.name}>{state.name}</option>
                                ))}
                            </Form.Control>
                        </Form.Group>


                        <Form.Group className="mb-3">
                            <Form.Label>City:</Form.Label>

                            <Form.Control className='form-select' as="select" name="city">
                                <option value="">Select City</option>
                                {cities.map((city, index) => (
                                    <option key={index} value={city}>{city}</option>
                                ))}
                            </Form.Control>
                        </Form.Group> */}

            <Form.Group className='mb-3'>
              <Form.Label>Country:</Form.Label>
              <Controller
                name='country'
                control={control}
                defaultValue={isEdit?.country || ''}
                rules={{
                  required: false,
                }}
                render={({ field }) => (
                  <Form.Control
                    {...field}
                    className='form-select'
                    as='select'
                    name='country'
                    value={field.value}
                    onChange={e => {
                      field.onChange(e)
                      handleCountryChange(e.target.value)
                    }}
                  >
                    <option value=''>Select Country</option>
                    {countries.map((country, index) => (
                      <option key={index} value={country}>
                        {country}
                      </option>
                    ))}
                  </Form.Control>
                )}
              />
            </Form.Group>

            <Form.Group className='mb-3'>
              <Form.Label>State:</Form.Label>
              <Controller
                name='state'
                control={control}
                defaultValue={isEdit?.state || ''}
                rules={{
                  required: false,
                }}
                render={({ field }) => (
                  <Form.Control
                    {...field}
                    className='form-select'
                    as='select'
                    name='state'
                    value={field.value}
                    onChange={e => {
                      field.onChange(e)
                      handleStateChange(e.target.value)
                    }}
                  >
                    <option value=''>Select State</option>
                    {states.map((state, index) => (
                      <option key={index} value={state.name}>
                        {state.name}
                      </option>
                    ))}
                  </Form.Control>
                )}
              />
            </Form.Group>

            <Form.Group className='mb-3'>
              <Form.Label>City:</Form.Label>
              <Controller
                name='city'
                control={control}
                defaultValue={isEdit?.city || ''}
                rules={{
                  required: false,
                }}
                render={({ field }) => (
                  <Form.Control {...field} className='form-select' as='select' name='city'>
                    <option value=''>Select City</option>
                    {cities.map((city, index) => (
                      <option key={index} value={city}>
                        {city}
                      </option>
                    ))}
                  </Form.Control>
                )}
              />
            </Form.Group>

            <Form.Group className='mb-3'>
              <Form.Label>zip code:</Form.Label>
              <Controller
                name='zip_code'
                control={control}
                defaultValue={isEdit?.zip_code || ''}
                rules={{
                  required: false,
                }}
                render={({ field }) => (
                  <Form.Control
                    {...field}
                    type='text'
                    placeholder='Enter zip code'
                    isInvalid={errors.zip_code}
                  />
                )}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer className='border-0'>
          <LoadingButtonWithForm
            isLoading={editLoading ? editLoading : addLoading}
            name='Save'
            form='driver-status'
          />
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default React.memo(DriversAddModal)
